import request from 'api'
import { LanguageDetectorAsyncModule } from 'i18next'

import { getDefaultLocaleForCountry, isChinaRegion } from '.'
import supportedLanguageListJson from './assets/json/supported-language-list.json'

const LOCALE_MAPPING = {
  'in-id': 'id-ID',
  'in-in': 'id-ID',
  'id-in': 'id-ID',
  in: 'id-ID',
  id: 'id-ID'
}

const normalizeLocale = (locale: string): string => {
  const lowerCaseLocale = locale.toLowerCase()
  return LOCALE_MAPPING[lowerCaseLocale] || locale
}

const CACHE_KEY = 'i18nextLng'

type SupportedLanguages = {
  group: string
  countryList: {
    countryName: string
    countryCode: string
    languages: {
      language: string
      locale: string
    }[]
  }[]
}[]

const supportedLanguageList: SupportedLanguages = supportedLanguageListJson

const isSupportedLocale = (locale: string) => {
  return supportedLanguageList.some((group) => {
    return group.countryList.some((country) => {
      return country.languages.some((language) => language.locale.includes(locale))
    })
  })
}

const detectFromQuerystring = () => {
  let countryCode
  let languageCode
  let locale
  let paramLocale

  if (typeof window !== 'undefined') {
    let { search } = window.location

    if (!window.location.search && window.location.hash && window.location.hash.indexOf('?') > -1) {
      search = window.location.hash.substring(window.location.hash.indexOf('?'))
    }
    const query = search.substring(1)
    const params = query.split('&')
    for (let i = 0; i < params.length; i++) {
      const pos = params[i].indexOf('=')
      if (pos > 0) {
        const key = params[i].substring(0, pos)
        if (key === 'locale') {
          locale = params[i].substring(pos + 1)
        } else if (key === 'countryCode') {
          countryCode = params[i].substring(pos + 1)
        } else if (key === 'languageCode') {
          languageCode = params[i].substring(pos + 1)
        } else if (key === 'paramLocale') {
          paramLocale = params[i].substring(pos + 1).replace('_', '-')
        }
      }
    }

    locale = locale || paramLocale

    if (locale) {
      const normalizedLocale = normalizeLocale(locale)
      if (isSupportedLocale(normalizedLocale)) {
        return normalizedLocale
      }
    }

    if (languageCode && countryCode) {
      const constructedLocale = languageCode + '-' + countryCode
      const normalizedLocale = normalizeLocale(constructedLocale)
      if (isSupportedLocale(normalizedLocale)) {
        return normalizedLocale
      }
    }
  }
  return undefined
}

let hasLocalStorageSupport: null | boolean = null
const localStorageAvailable = () => {
  if (hasLocalStorageSupport !== null) return hasLocalStorageSupport

  try {
    hasLocalStorageSupport = window !== undefined && window.localStorage !== null
    const testKey = 'i18next.translate.boo'
    window.localStorage.setItem(testKey, 'foo')
    window.localStorage.removeItem(testKey)
  } catch (e) {
    hasLocalStorageSupport = false
  }
  return hasLocalStorageSupport
}
const detectFromStorage = () => {
  if (localStorageAvailable()) {
    const lng = window.localStorage.getItem(CACHE_KEY)
    if (lng) {
      const normalizedLocale = normalizeLocale(lng)
      if (isSupportedLocale(normalizedLocale)) {
        return normalizedLocale
      }
    }
  }
  return undefined
}

const detectFromGeographicIp = async () => {
  let countryCode
  try {
    const response = await request.get('/v1/countries')
    countryCode = response.data.countryCode
  } catch (error) {
    return undefined
  }
  if (countryCode) {
    const locale = getDefaultLocaleForCountry(countryCode)
    return locale ? normalizeLocale(locale) : undefined
  }
  return undefined
}

const detectFromBrowser = () => {
  const found: string[] = []

  if (typeof navigator !== 'undefined') {
    if (navigator.languages) {
      // chrome only; not an array, so can't use .push.apply instead of iterating
      for (let i = 0; i < navigator.languages.length; i++) {
        const normalizedLocale = normalizeLocale(navigator.languages[i])
        if (isSupportedLocale(normalizedLocale)) {
          found.push(normalizedLocale)
        }
      }
    }
    if (navigator.language) {
      const normalizedLocale = normalizeLocale(navigator.language)
      if (isSupportedLocale(normalizedLocale)) {
        found.push(normalizedLocale)
      }
    }
  }
  return found.length > 0 ? found : undefined
}

export const languageDetector: LanguageDetectorAsyncModule = {
  type: 'languageDetector',
  async: true,
  init: () => {},
  detect: async (setLanguage) => {
    let locale

    if (isChinaRegion) {
      setLanguage(`zh-CN`)
      return
    }

    locale = detectFromQuerystring()
    if (locale) {
      setLanguage(locale)
      return
    }

    locale = detectFromStorage()
    if (locale) {
      setLanguage(locale)
      return
    }

    locale = await detectFromGeographicIp()
    if (locale) {
      setLanguage(locale)
      return
    }

    locale = detectFromBrowser()
    if (locale) {
      setLanguage(locale)
      return
    }
  },
  cacheUserLanguage: (lng) => {
    window.localStorage.setItem(CACHE_KEY, lng)
  }
}
